// src/components/resumo/EditorToolbar.tsx
import React, { useState, useRef } from 'react';
import {
    Bold, Italic, Underline, Strikethrough,
    Quote,
    List, ListOrdered,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Palette, Highlighter, Superscript, Subscript, StickyNote, Eraser,
    Image, Video, Film, Sparkles, Table, FileQuestion, Undo2, Redo2, X, Music, Link2,
    IndentIncrease, IndentDecrease, ChevronDown, ImagePlus
} from 'lucide-react';
import { ResumoPage } from './components/PageNavigator';
import './EditorToolbar.css';
import { InlineContentManager } from './InlineContentManager';
import TableInsertModal from './TableInsertModal';
import { TableManager } from './TableManager'; // ← Novo manager
import { MediaResizeManager } from './MediaResizeManager';
import { getCalloutIconSvg } from './calloutIcons';

interface EditorToolbarProps {
    cards?: any[];
    darkMode?: boolean;
    onSaveSelection: () => void;
    savedRangeRef: React.MutableRefObject<Range | null>;
    editorRef: React.RefObject<HTMLDivElement | null>;
    contentManager?: InlineContentManager | null;
    quizManager?: any;
    tableManager?: TableManager | null;
    resizeManager?: MediaResizeManager | null;
    pages?: ResumoPage[];
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
    cards = [],
    darkMode = false,
    onSaveSelection,
    savedRangeRef,
    editorRef,
    contentManager,
    quizManager,
    tableManager,
    resizeManager,
    pages = [],
}) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showBgPicker, setShowBgPicker] = useState(false);
    const [showCardModal, setShowCardModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [mediaType, setMediaType] = useState<'image' | 'video' | 'gif'>('image');
    const [mediaUrl, setMediaUrl] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [mediaCaption, setMediaCaption] = useState('');
    const [showTableModal, setShowTableModal] = useState(false);
    const [badgeColor, setBadgeColor] = useState(() => getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130');
    const [showQuotePicker, setShowQuotePicker] = useState(false);
    const [quizColor, setQuizColor] = useState(() => getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130');
    const [showQuizColorPicker, setShowQuizColorPicker] = useState(false);

    const [showCalloutModal, setShowCalloutModal] = useState(false);
    const [calloutTitle, setCalloutTitle] = useState('');
    const [calloutText, setCalloutText] = useState('');
    const [calloutColor, setCalloutColor] = useState(() => getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130');
    const [calloutBgColor, setCalloutBgColor] = useState('#e8f5e9');
    const [calloutBorderStyle, setCalloutBorderStyle] = useState<'left' | 'full' | 'top'>('left');
    const [showMarkerPicker, setShowMarkerPicker] = useState(false);
    const [calloutIcon, setCalloutIcon] = useState('');
    const [calloutIsGabarito, setCalloutIsGabarito] = useState(false);
    const calloutEditorRef = useRef<HTMLDivElement>(null);
    const calloutTitleRef = useRef<HTMLDivElement>(null);

    const [showAudioModal, setShowAudioModal] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const [audioCaption, setAudioCaption] = useState('');
    const [audioUploading, setAudioUploading] = useState(false);
    const [audioUploadError, setAudioUploadError] = useState('');
    const audioFileInputRef = useRef<HTMLInputElement>(null);

    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showMediaDropdown, setShowMediaDropdown] = useState(false);
    const [linkTab, setLinkTab] = useState<'page' | 'apostila'>('page');
    const [linkTargetPageId, setLinkTargetPageId] = useState('');
    const [linkExternalUrl, setLinkExternalUrl] = useState('');

    const [currentFontSize, setCurrentFontSize] = useState('');
    const [currentFontFamily, setCurrentFontFamily] = useState('');

    // Detecta fonte e tamanho do texto no cursor/seleção
    React.useEffect(() => {
        const handleSelectionChange = () => {
            if (!editorRef.current) return;
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            if (!editorRef.current.contains(range.commonAncestorContainer)) return;

            let node: Node | null = selection.anchorNode;
            let detectedFamily = '';
            let detectedSize = '';

            while (node && node !== editorRef.current) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const el = node as HTMLElement;
                    if (!detectedFamily && el.tagName === 'FONT' && el.getAttribute('face')) {
                        detectedFamily = el.getAttribute('face') || '';
                    }
                    if (!detectedFamily && el.style?.fontFamily) {
                        detectedFamily = el.style.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
                    }
                    if (!detectedSize && el.style?.fontSize) {
                        detectedSize = el.style.fontSize;
                    }
                }
                node = node.parentNode;
            }

            setCurrentFontFamily(detectedFamily);
            setCurrentFontSize(detectedSize);
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, [editorRef]);

    const quizColors = [
        { label: 'Verde GRAIA', value: '#0D3B0E' },
        { label: 'Azul prussiano', value: '#2a4a5a' },
        { label: 'Verde médio', value: '#2F7130' },
        { label: 'Verde claro', value: '#16a34a' },
        { label: 'Azul', value: '#2563eb' },
        { label: 'Azul claro', value: '#0891b2' },
        { label: 'Roxo', value: '#7c3aed' },
        { label: 'Lilás', value: '#9333ea' },
        { label: 'Rosa', value: '#db2777' },
        { label: 'Vermelho', value: '#dc2626' },
        { label: 'Laranja', value: '#ea580c' },
        { label: 'Amarelo', value: '#ca8a04' },
        { label: 'Cinza', value: '#4b5563' },
        { label: 'Preto', value: '#111827' },
    ];

    const quoteColors = [
        { label: 'Verde GRAIA', value: '#0D3B0E' },
        { label: 'Azul prussiano', value: '#2a4a5a' },
        { label: 'Verde', value: '#2F7130' },
        { label: 'Azul', value: '#2563eb' },
        { label: 'Roxo', value: '#7c3aed' },
        { label: 'Rosa', value: '#db2777' },
        { label: 'Vermelho', value: '#dc2626' },
        { label: 'Laranja', value: '#ea580c' },
        { label: 'Cinza', value: '#6b7280' },
    ];

    const markerColors = [
        // Pretos e cinzas
        '#000000', '#374151', '#6b7280', '#9ca3af',
        // Vermelhos
        '#ef4444', '#dc2626', '#b91c1c',
        // Laranjas
        '#f97316', '#ea580c',
        // Amarelos
        '#f59e0b', '#ca8a04',
        // Verdes
        '#0D3B0E', '#2F7130', '#16a34a', '#15803d', '#166534', '#5FBA61', '#86efac',
        // Azuis
        '#2a4a5a', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#0891b2', '#0e7490',
        // Roxos/Rosa
        '#8b5cf6', '#7c3aed', '#ec4899', '#db2777',
        // Branco
        '#ffffff',
    ];

    const badgeColors = [
        { label: 'Verde GRAIA', value: '#0D3B0E' },
        { label: 'Azul prussiano', value: '#2a4a5a' },
        { label: 'Verde médio', value: '#2F7130' },
        { label: 'Verde claro', value: '#16a34a' },
        { label: 'Azul', value: '#2563eb' },
        { label: 'Azul claro', value: '#0891b2' },
        { label: 'Roxo', value: '#7c3aed' },
        { label: 'Lilás', value: '#9333ea' },
        { label: 'Rosa', value: '#db2777' },
        { label: 'Vermelho', value: '#dc2626' },
        { label: 'Laranja', value: '#ea580c' },
        { label: 'Amarelo', value: '#ca8a04' },
        { label: 'Cinza', value: '#4b5563' },
        { label: 'Preto', value: '#111827' },
    ];

    const calloutPresets = [
        { label: 'Verde GRAIA', border: '#0D3B0E', bg: '#e0ede0', text: '#0D3B0E' },
        { label: 'Azul prussiano', border: '#2a4a5a', bg: '#dce8f0', text: '#2a4a5a' },
        { label: 'Verde (padrão)', border: '#2F7130', bg: '#e8f5e9', text: '#1b5e20' },
        { label: 'Azul', border: '#2563eb', bg: '#dbeafe', text: '#1e3a8a' },
        { label: 'Roxo', border: '#7c3aed', bg: '#ede9fe', text: '#4c1d95' },
        { label: 'Vermelho', border: '#dc2626', bg: '#fee2e2', text: '#7f1d1d' },
        { label: 'Amarelo', border: '#ca8a04', bg: '#fef9c3', text: '#78350f' },
        { label: 'Cinza', border: '#4b5563', bg: '#f3f4f6', text: '#1f2937' },
        { label: 'Rosa', border: '#db2777', bg: '#fce7f3', text: '#831843' },
        { label: 'Laranja', border: '#ea580c', bg: '#ffedd5', text: '#7c2d12' },
    ];

    const calloutIcons = [
        { label: 'Nenhum', value: '' },
        { label: 'Bandeira', value: 'flag' },
        { label: 'Check', value: 'check-circle' },
        { label: 'Atenção', value: 'alert-triangle' },
        { label: 'Info', value: 'info' },
        { label: 'Estrela', value: 'star' },
        { label: 'Lâmpada', value: 'lightbulb' },
        { label: 'Alvo', value: 'target' },
        { label: 'Raio', value: 'zap' },
        { label: 'Pin', value: 'pin' },
        { label: 'Livro', value: 'book-open' },
        { label: 'Troféu', value: 'trophy' },
        { label: 'Engrenagem', value: 'settings' },
        { label: 'Lupa', value: 'search' },
        { label: 'Cadeado', value: 'lock' },
        { label: 'Coração', value: 'heart' },
        { label: 'Seta', value: 'arrow-right' },
        { label: 'Clip', value: 'paperclip' },
        { label: 'Bookmark', value: 'bookmark' },
        { label: 'Microscópio', value: 'microscope' },
        { label: 'Termômetro', value: 'thermometer' },
        { label: 'Atividade', value: 'activity' },
        { label: 'Shield', value: 'shield' },
        { label: 'Alerta', value: 'alert-circle' },
    ];

    const textColors = [
        // Pretos e cinzas
        '#000000', '#374151', '#6b7280', '#9ca3af', '#ffffff',
        // Vermelhos
        '#ef4444', '#dc2626', '#fca5a5',
        // Laranjas/Amarelos
        '#f97316', '#f59e0b', '#fde047',
        // Verdes — tons variados
        '#0D3B0E', '#2F7130', '#166534', '#15803d', '#16a34a', '#22c55e',
        '#5FBA61', '#86efac', '#bbf7d0', '#dcfce7',
        // Azuis — tons variados
        '#2a4a5a', '#1e3a8a', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa',
        '#93c5fd', '#0e7490', '#0891b2', '#06b6d4', '#bfdbfe',
        // Roxos/Rosa
        '#8b5cf6', '#7c3aed', '#ec4899', '#d8b4fe',
    ];

    const bgColors = [
        'transparent',
        // Amarelos
        '#fef08a', '#fde047', '#fef3c7',
        // Laranjas
        '#fed7aa', '#fdba74', '#fca5a5',
        // Verdes
        '#e0ede0', '#bbf7d0', '#86efac', '#d1fae5',
        // Azuis
        '#dce8f0', '#bfdbfe', '#93c5fd', '#dbeafe',
        // Roxos/Rosa
        '#e9d5ff', '#f9a8d4', '#fce7f3',
        // Cinzas
        '#f3f4f6', '#374151'
    ];

    // ====== LIMPAR FORMATAÇÃO AGRESSIVO ======
    const clearFormatting = () => {
        const editor = editorRef.current;
        if (!editor) return;

        onSaveSelection();

        const selection = window.getSelection();
        let range: Range | null = null;

        if (savedRangeRef.current && editor.contains(savedRangeRef.current.commonAncestorContainer)) {
            range = savedRangeRef.current;
        } else if (selection && selection.rangeCount > 0) {
            range = selection.getRangeAt(0);
        }

        if (!range) { editor.focus(); return; }

        // Restaurar seleção
        selection?.removeAllRanges();
        selection?.addRange(range);

        // 1. removeFormat padrão (remove negrito/itálico/etc.)
        document.execCommand('removeFormat', false);
        document.execCommand('unlink', false);

        // 2. Resetar fonte e tamanho via execCommand
        document.execCommand('fontName', false, 'Georgia');
        // fontSize 3 = médio; depois sobreescrevemos com style
        document.execCommand('fontSize', false, '3');

        // 3. Substituir <font size="3"> por span com 16px
        editor.querySelectorAll<HTMLElement>('font[size="3"]').forEach(el => {
            el.removeAttribute('size');
            (el as HTMLElement).style.fontSize = '16px';
        });

        // 4. Limpar estilos inline dos nós dentro do range
        const applyClean = (node: HTMLElement) => {
            // Preservar apenas display e estrutura necessária
            const tag = node.tagName?.toLowerCase();
            const keepTags = ['table', 'thead', 'tbody', 'tr', 'td', 'th', 'img', 'video', 'audio'];
            if (keepTags.includes(tag)) return;

            node.style.removeProperty('font-weight');
            node.style.removeProperty('font-style');
            node.style.removeProperty('text-decoration');
            node.style.removeProperty('background-color');
            node.style.removeProperty('color');
            node.style.removeProperty('letter-spacing');
            node.style.removeProperty('line-height');
            node.style.removeProperty('margin');
            node.style.removeProperty('padding');
            node.style.removeProperty('text-indent');
            // Manter font-size e font-family (já definidos pelo resetFont acima)
        };

        // Nó âncora e todos os pais até o editor
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editor) {
            if (node.nodeType === Node.ELEMENT_NODE) applyClean(node as HTMLElement);
            node = node.parentNode;
        }

        // Também limpa todos os elementos dentro do range selecionado
        const container = range.commonAncestorContainer;
        if (container.nodeType === Node.ELEMENT_NODE) {
            (container as HTMLElement).querySelectorAll<HTMLElement>('*').forEach(applyClean);
        }

        // 5. Cor preta explicitamente
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand('foreColor', false, '#000000');
        document.execCommand('backColor', false, 'transparent');

        const inputEvent = new Event('input', { bubbles: true });
        editor.dispatchEvent(inputEvent);

        setCurrentFontFamily('Georgia');
        setCurrentFontSize('16px');
    };

    // ====== APLICAR PRESET DE ESTILO ======
    const applyStylePreset = (color: string, fontFamily: string, fontSize: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        onSaveSelection();

        const selection = window.getSelection();
        const range = savedRangeRef.current || (selection?.rangeCount ? selection.getRangeAt(0) : null);
        if (!range) return;

        selection?.removeAllRanges();
        selection?.addRange(range);

        // Aplicar cor
        document.execCommand('foreColor', false, color);

        // Aplicar fonte
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand('fontName', false, fontFamily);

        // Aplicar tamanho
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand('fontSize', false, '7');
        editor.querySelectorAll<HTMLElement>('font[size="7"]').forEach(el => {
            el.removeAttribute('size');
            el.style.fontSize = fontSize;
        });

        // Aplicar negrito (sempre ativo no preset)
        selection?.removeAllRanges();
        selection?.addRange(range);
        if (!document.queryCommandState('bold')) {
            document.execCommand('bold', false);
        }

        const inputEvent = new Event('input', { bubbles: true });
        editor.dispatchEvent(inputEvent);

        setCurrentFontFamily(fontFamily);
        setCurrentFontSize(fontSize);
    };

    // ====== INDENT / OUTDENT ======
    const handleIndent = () => {
        onSaveSelection();
        const editor = editorRef.current;
        if (!editor) return;

        const sel = window.getSelection();
        if (savedRangeRef.current) {
            sel?.removeAllRanges();
            sel?.addRange(savedRangeRef.current);
        }

        // Verificar se estamos em uma lista — se sim, usa indent nativo
        let node: Node | null = sel?.anchorNode || null;
        let inList = false;
        while (node && node !== editor) {
            if ((node as HTMLElement).tagName === 'LI' ||
                (node as HTMLElement).tagName === 'UL' ||
                (node as HTMLElement).tagName === 'OL') {
                inList = true;
                break;
            }
            node = node.parentNode;
        }

        if (inList) {
            editor.focus();
            document.execCommand('indent', false);
        } else {
            // Para texto normal: aumentar padding-left do bloco pai
            let blockNode: Node | null = sel?.anchorNode || null;
            while (blockNode && blockNode !== editor) {
                const tag = (blockNode as HTMLElement).tagName;
                if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'].includes(tag)) {
                    const el = blockNode as HTMLElement;
                    const current = parseInt(el.style.paddingLeft || '0');
                    el.style.paddingLeft = `${current + 24}px`;
                    break;
                }
                blockNode = blockNode.parentNode;
            }
            const inputEvent = new Event('input', { bubbles: true });
            editor.dispatchEvent(inputEvent);
        }
    };

    const handleOutdent = () => {
        onSaveSelection();
        const editor = editorRef.current;
        if (!editor) return;

        const sel = window.getSelection();
        if (savedRangeRef.current) {
            sel?.removeAllRanges();
            sel?.addRange(savedRangeRef.current);
        }

        let node: Node | null = sel?.anchorNode || null;
        let inList = false;
        while (node && node !== editor) {
            if ((node as HTMLElement).tagName === 'LI' ||
                (node as HTMLElement).tagName === 'UL' ||
                (node as HTMLElement).tagName === 'OL') {
                inList = true;
                break;
            }
            node = node.parentNode;
        }

        if (inList) {
            editor.focus();
            document.execCommand('outdent', false);
        } else {
            let blockNode: Node | null = sel?.anchorNode || null;
            while (blockNode && blockNode !== editor) {
                const tag = (blockNode as HTMLElement).tagName;
                if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'].includes(tag)) {
                    const el = blockNode as HTMLElement;
                    const current = parseInt(el.style.paddingLeft || '0');
                    el.style.paddingLeft = `${Math.max(0, current - 24)}px`;
                    break;
                }
                blockNode = blockNode.parentNode;
            }
            const inputEvent = new Event('input', { bubbles: true });
            editor.dispatchEvent(inputEvent);
        }
    };

    // ====== TAMANHO DO MARCADOR DE LISTA ======
    const applyListMarkerSize = (size: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const sel = window.getSelection();
        let node: Node | null = sel?.anchorNode || null;
        while (node && node !== editor) {
            const tag = (node as HTMLElement).tagName;
            if (tag === 'UL' || tag === 'OL') {
                const listEl = node as HTMLElement;
                listEl.style.setProperty('--marker-size', size);
                // Aplicar a todos os li
                listEl.querySelectorAll<HTMLElement>('li').forEach(li => {
                    li.style.fontSize = size;
                });
                const inputEvent = new Event('input', { bubbles: true });
                editor.dispatchEvent(inputEvent);
                return;
            }
            if (tag === 'LI') {
                (node as HTMLElement).style.fontSize = size;
                const inputEvent = new Event('input', { bubbles: true });
                editor.dispatchEvent(inputEvent);
                return;
            }
            node = node.parentNode;
        }
    };

    const execCommand = (command: string, value?: string) => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (savedRangeRef.current) {
            const container = savedRangeRef.current.commonAncestorContainer;
            const node = container.nodeType === Node.ELEMENT_NODE ? container as Element : container.parentElement;
            const calloutBody = node?.closest('.callout-body');
            const calloutTitle = node?.closest('.callout-title');
            if (calloutTitle) {
                (calloutTitle as HTMLElement).focus();
                selection?.removeAllRanges();
                selection?.addRange(savedRangeRef.current);
            } else if (calloutBody) {
                (calloutBody as HTMLElement).focus();
                selection?.removeAllRanges();
                selection?.addRange(savedRangeRef.current);
            } else {
                editorRef.current.focus();
            }
        } else {
            editorRef.current.focus();
        }
        document.execCommand(command, false, value);
    };

    // Alinhamento: prioriza imagem selecionada, depois text alignment normal
    const applyAlign = (justify: string, imgAlign: 'left' | 'center' | 'right') => {
        const editor = editorRef.current;
        if (editor && resizeManager) {
            const selected = editor.querySelector('.media-container.selected') as HTMLElement | null;
            if (selected) {
                resizeManager.applyAlignment(selected, imgAlign);
                return;
            }
        }
        execCommand(justify);
    };

    const insertFlashcardBadge = (card: any, index: number) => {
        console.log('🎯 insertFlashcardBadge chamado:', {
            card,
            index,
            hasContentManager: !!contentManager
        });

        if (!contentManager) {
            console.error('❌ ContentManager não está disponível!');
            alert('Erro: ContentManager não inicializado.');
            return;
        }

        try {
            contentManager.insertBadge(
                card._id,
                index,
                `Card ${index + 1}`,
                savedRangeRef.current,
                badgeColor
            );
            setShowCardModal(false);
            console.log('✅ Badge inserido com sucesso');
        } catch (error) {
            console.error('❌ Erro ao inserir badge:', error);
        }
    };

    const insertMedia = async (type: 'image' | 'video' | 'gif', source: File | string, caption?: string) => {
        console.log('🖼️ insertMedia chamado:', {
            type,
            source: source instanceof File ? source.name : source,
            caption,
            hasContentManager: !!contentManager
        });

        if (!contentManager) {
            console.error('❌ ContentManager não está disponível!');
            alert('Erro: ContentManager não inicializado.');
            return;
        }

        try {
            await contentManager.insertMedia(type, source, caption, savedRangeRef.current);
            setShowMediaModal(false);
            setMediaUrl('');
            setMediaCaption('');
            console.log('✅ Mídia inserida com sucesso');
        } catch (error) {
            console.error('❌ Erro ao inserir mídia:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        insertMedia(mediaType, file, mediaCaption);
        e.target.value = '';
    };

    const handleInsertQuiz = () => {
        if (!quizManager) {
            console.warn('⚠️ QuizManager não disponível');
            return;
        }

        onSaveSelection();

        const quizId = quizManager.insertQuizBadge(quizColor);
        if (quizId) {
            console.log('✅ Quiz inserido:', quizId);
            if (editorRef.current) {
                editorRef.current.focus();
            }
        } else {
            console.error('❌ Erro ao inserir quiz');
        }
    };

    const isInsideBlockquote = (): boolean => {
        const selection = window.getSelection();
        if (!selection || !editorRef.current) return false;
        let node: Node | null = selection.anchorNode;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'BLOCKQUOTE') return true;
            node = node.parentNode;
        }
        return false;
    };

    const handleQuoteClick = () => {
        onSaveSelection();
        if (!isInsideBlockquote()) {
            execCommand('formatBlock', '<blockquote>');
        }
        setShowQuotePicker(true);
    };

    const applyQuoteColor = (color: string) => {
        const selection = window.getSelection();
        if (!selection || !editorRef.current) return;

        let node: Node | null = selection.anchorNode;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'BLOCKQUOTE') {
                (node as HTMLElement).style.borderLeftColor = color;
                (node as HTMLElement).style.color = color === '#6b7280' ? '#6b7280' : color;
                break;
            }
            node = node.parentNode;
        }
        setShowQuotePicker(false);
    };

    const removeQuote = () => {
        const selection = window.getSelection();
        if (!selection || !editorRef.current) return;
        let node: Node | null = selection.anchorNode;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'BLOCKQUOTE') {
                const blockquote = node as HTMLElement;
                const parent = blockquote.parentNode;
                if (parent) {
                    while (blockquote.firstChild) {
                        parent.insertBefore(blockquote.firstChild, blockquote);
                    }
                    parent.removeChild(blockquote);
                }
                break;
            }
            node = node.parentNode;
        }
        const inputEvent = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(inputEvent);
        setShowQuotePicker(false);
    };

    const insertAudioHTML = (src: string, caption?: string) => {
        if (!editorRef.current) return;

        const mediaId = `audio-${Date.now()}`;
        const captionHtml = caption
            ? `<div style="font-size:12px;color:#6b7280;font-style:italic;margin-top:6px;text-align:center;">${caption}</div>`
            : '';

        const audioHtml = `<div
            class="media-container audio-container"
            data-media-id="${mediaId}"
            data-media-type="audio"
            contenteditable="false"
            style="display:block;margin:12px 0;position:relative;"
        ><audio src="${src}" controls style="width:100%;max-width:500px;display:block;border-radius:8px;outline:none;"></audio>${captionHtml}</div><p><br></p>`;

        editorRef.current.focus();

        const selection = window.getSelection();
        let inserted = false;

        if (savedRangeRef.current && editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)) {
            try {
                const range = savedRangeRef.current;
                selection?.removeAllRanges();
                selection?.addRange(range);
                range.deleteContents();
                const temp = document.createElement('div');
                temp.innerHTML = audioHtml;
                const frag = document.createDocumentFragment();
                let node;
                while ((node = temp.firstChild)) frag.appendChild(node);
                range.insertNode(frag);
                inserted = true;
            } catch (e) {
                console.warn('Range inválido, inserindo no final');
            }
        }

        if (!inserted) {
            editorRef.current.innerHTML += audioHtml;
        }

        const inputEvent = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(inputEvent);

        setShowAudioModal(false);
        setAudioUrl('');
        setAudioCaption('');
    };

    const handleAudioFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';

        setAudioUploadError('');
        setAudioUploading(true);

        try {
            const token = localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('audio', file);

            const response = await fetch('/api/upload/resumo-audio-cloudinary', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || 'Erro ao fazer upload do áudio');
            }

            const data = await response.json();
            insertAudioHTML(data.url, audioCaption || undefined);
        } catch (err: any) {
            setAudioUploadError(err.message || 'Erro ao enviar áudio');
        } finally {
            setAudioUploading(false);
        }
    };

    const insertInternalLink = () => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        let range: Range | null = null;
        if (savedRangeRef.current && editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)) {
            range = savedRangeRef.current;
        } else if (selection && selection.rangeCount > 0) {
            const r = selection.getRangeAt(0);
            if (editorRef.current.contains(r.commonAncestorContainer)) range = r;
        }

        if (!range || range.collapsed) {
            alert('Selecione um texto primeiro para criar o link.');
            return;
        }

        const selectedText = range.toString().trim();
        if (!selectedText) return;

        let dataAttrs = '';
        if (linkTab === 'page') {
            if (!linkTargetPageId) return;
            const targetPage = pages.find(p => p.id === linkTargetPageId);
            const pageName = targetPage?.title || `Página ${pages.findIndex(p => p.id === linkTargetPageId) + 1}`;
            dataAttrs = `data-link-type="page" data-page-id="${linkTargetPageId}" data-page-name="${pageName}"`;
        } else {
            if (!linkExternalUrl.trim()) return;
            dataAttrs = `data-link-type="apostila" data-href="${linkExternalUrl.trim()}"`;
        }

        const linkHtml = `<a class="resumo-internal-link" ${dataAttrs} style="color:var(--platform-accent);font-weight:600;text-decoration:underline;cursor:pointer;border-radius:3px;padding:1px 3px;background:var(--platform-a08);">${selectedText}</a>`;

        selection?.removeAllRanges();
        selection?.addRange(range);
        range.deleteContents();

        const temp = document.createElement('div');
        temp.innerHTML = linkHtml;
        const frag = document.createDocumentFragment();
        let node;
        while ((node = temp.firstChild)) frag.appendChild(node);
        range.insertNode(frag);

        const inputEvent = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(inputEvent);

        setShowLinkModal(false);
        setLinkTargetPageId('');
        setLinkExternalUrl('');
    };

    const insertCallout = () => {
        if (!editorRef.current) return;

        // Conteúdo rico: ler innerHTML do editor contenteditable
        const richBodyHtml = calloutEditorRef.current?.innerHTML?.trim() || calloutText || 'Digite o conteúdo aqui...';

        const iconSvg = calloutIcon ? getCalloutIconSvg(calloutIcon, calloutColor) : '';
        const iconHtml = iconSvg
            ? `<span style="display:inline-flex;align-items:center;margin-right:8px;flex-shrink:0;" data-callout-icon-name="${calloutIcon}">${iconSvg}</span>`
            : '';

        const richTitleHtml = calloutTitleRef.current?.innerHTML?.trim() || '';
        const titleIsEmpty = !calloutTitleRef.current?.textContent?.trim();
        const titleHtml = !titleIsEmpty
            ? `<p style="margin: 0 0 8px 0; font-weight: 700; font-size: 15px; color: ${calloutColor};" class="callout-title">${iconHtml}${richTitleHtml}</p>`
            : (calloutIcon ? `<p style="margin: 0 0 8px 0; font-weight: 700; font-size: 15px; color: ${calloutColor};" class="callout-title">${iconHtml}</p>` : '');

        const borderCss = calloutBorderStyle === 'left'
            ? `border-left: 4px solid ${calloutColor};`
            : calloutBorderStyle === 'full'
                ? `border: 2px solid ${calloutColor};`
                : `border-top: 4px solid ${calloutColor};`;

        const calloutHtml = `<div
        class="resumo-callout"
        contenteditable="false"
        data-callout="true"
        data-gabarito="${calloutIsGabarito}"
        data-callout-color="${calloutColor}"
        data-callout-bg="${calloutBgColor}"
        data-callout-icon="${calloutIcon}"
        style="display: block; ${borderCss} background: ${calloutBgColor}; border-radius: 8px; padding: 16px 20px; margin: 16px 0; position: relative; cursor: default;"
    >${titleHtml}<div style="margin: 0; font-size: 15px; line-height: 1.7; color: ${calloutColor};" class="callout-body">${richBodyHtml}</div></div><p><br></p>`;

        editorRef.current.focus();

        const selection = window.getSelection();
        let inserted = false;

        if (savedRangeRef.current && editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)) {
            try {
                const range = savedRangeRef.current;
                selection?.removeAllRanges();
                selection?.addRange(range);
                range.deleteContents();
                const temp = document.createElement('div');
                temp.innerHTML = calloutHtml;
                const frag = document.createDocumentFragment();
                let node;
                while ((node = temp.firstChild)) frag.appendChild(node);
                range.insertNode(frag);
                inserted = true;
            } catch (e) {
                console.warn('Range inválido, inserindo no final');
            }
        }

        if (!inserted) {
            editorRef.current.innerHTML += calloutHtml;
        }

        const inputEvent = new Event('input', { bubbles: true });
        editorRef.current.dispatchEvent(inputEvent);

        setShowCalloutModal(false);
        setCalloutTitle('');
        setCalloutText('');
        setCalloutColor(getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130');
        setCalloutBgColor('#e8f5e9');
        setCalloutIcon('');
        setCalloutIsGabarito(false);
        if (calloutEditorRef.current) calloutEditorRef.current.innerHTML = '';
        if (calloutTitleRef.current) calloutTitleRef.current.innerHTML = '';

        setTimeout(() => {
            contentManager?.initializeAllCallouts?.();
        }, 100);
    };


    return (
        <>
            <div
                className={`et-toolbar ${darkMode ? 'dark' : ''}`}
                onMouseDown={() => {
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                }}
                onClick={(e) => {
                    // Fechar dropdown de mídia ao clicar fora do botão unificado
                    if (!(e.target as HTMLElement).closest('.color-picker-wrapper')) {
                        setShowMediaDropdown(false);
                    }
                }}
            >
                <div className="et-toolbar-section">
                    {/* Formatação Básica */}
                    <div className="toolbar-group">
                        <button className="et-btn" onClick={() => execCommand('bold')} title="Negrito (Ctrl+B)">
                            <Bold size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('italic')} title="Itálico (Ctrl+I)">
                            <Italic size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('underline')} title="Sublinhado (Ctrl+U)">
                            <Underline size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('strikeThrough')} title="Tachado">
                            <Strikethrough size={16} />
                        </button>
                        <button
                            className="et-btn"
                            onClick={clearFormatting}
                            title="Limpar formatação completa — reseta para Georgia 16px preto"
                        >
                            <Eraser size={16} />
                        </button>
                    </div>

                    {/* Desfazer / Refazer */}
                    <div className="toolbar-group">
                        <button
                            className="et-btn"
                            onClick={() => { editorRef.current?.focus(); document.execCommand('undo'); }}
                            title="Desfazer (Ctrl+Z)"
                        >
                            <Undo2 size={16} />
                        </button>
                        <button
                            className="et-btn"
                            onClick={() => { editorRef.current?.focus(); document.execCommand('redo'); }}
                            title="Refazer (Ctrl+Y)"
                        >
                            <Redo2 size={16} />
                        </button>
                    </div>

                    {/* Cores */}
                    <div className="toolbar-group">
                        <div className="color-picker-wrapper">
                            <button
                                className="et-btn"
                                onClick={() => {
                                    onSaveSelection();
                                    setShowColorPicker(!showColorPicker);
                                    setShowBgPicker(false);
                                }}
                                title="Cor do Texto"
                            >
                                <Palette size={16} />
                            </button>
                            {showColorPicker && (
                                <div className="color-picker-dropdown">
                                    {textColors.map(color => (
                                        <button
                                            key={color}
                                            className="color-option"
                                            style={{
                                                background: color,
                                                border: color === '#ffffff' ? '1px solid #e5e7eb' : 'none'
                                            }}
                                            onClick={() => {
                                                execCommand('foreColor', color);
                                                setShowColorPicker(false);
                                            }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="color-picker-wrapper">
                            <button
                                className="et-btn"
                                onClick={() => {
                                    onSaveSelection();
                                    setShowBgPicker(!showBgPicker);
                                    setShowColorPicker(false);
                                }}
                                title="Destacar Texto"
                            >
                                <Highlighter size={16} />
                            </button>
                            {showBgPicker && (
                                <div className="color-picker-dropdown">
                                    {bgColors.map(color => (
                                        <button
                                            key={color}
                                            className="color-option"
                                            style={{
                                                background: color === 'transparent'
                                                    ? 'repeating-linear-gradient(45deg, #f3f4f6, #f3f4f6 5px, #e5e7eb 5px, #e5e7eb 10px)'
                                                    : color
                                            }}
                                            onClick={() => {
                                                execCommand('backColor', color);
                                                setShowBgPicker(false);
                                            }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Títulos */}
                    <div className="toolbar-group">

                        {/* Tamanho de fonte e família */}
                        <div className="toolbar-group">
                            <select
                                className="et-font-size-select"
                                title="Tamanho da fonte"
                                value={currentFontSize}
                                onChange={(e) => {
                                    const size = e.target.value;
                                    if (size) {
                                        execCommand('fontSize', '7');
                                        const fontEls = editorRef.current?.querySelectorAll('font[size="7"]');
                                        fontEls?.forEach(el => {
                                            (el as HTMLElement).removeAttribute('size');
                                            (el as HTMLElement).style.fontSize = size;
                                        });
                                        setCurrentFontSize(size);
                                    }
                                }}
                            >
                                <option value="">Tam.</option>
                                <option value="8px">8</option>
                                <option value="9px">9</option>
                                <option value="10px">10</option>
                                <option value="11px">11</option>
                                <option value="12px">12</option>
                                <option value="13px">13</option>
                                <option value="14px">14</option>
                                <option value="16px">16</option>
                                <option value="18px">18</option>
                                <option value="20px">20</option>
                                <option value="22px">22</option>
                                <option value="24px">24</option>
                                <option value="28px">28</option>
                                <option value="32px">32</option>
                                <option value="36px">36</option>
                                <option value="40px">40</option>
                                <option value="48px">48</option>
                                <option value="60px">60</option>
                                <option value="72px">72</option>
                            </select>
                            <select
                                className="et-font-family-select"
                                title="Família de fonte"
                                value={currentFontFamily}
                                onChange={(e) => {
                                    const font = e.target.value;
                                    if (font) {
                                        onSaveSelection();
                                        execCommand('fontName', font);
                                        setCurrentFontFamily(font);
                                    }
                                }}
                            >
                                <option value="">Fonte</option>
                                <optgroup label="Sem serifa">
                                    <option value="Inter">Inter</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Trebuchet MS">Trebuchet MS</option>
                                </optgroup>
                                <optgroup label="Serifada">
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Palatino Linotype">Palatino</option>
                                    <option value="Cambria">Cambria</option>
                                </optgroup>
                                <optgroup label="Monoespaçada">
                                    <option value="Courier New">Courier New</option>
                                </optgroup>
                            </select>
                        </div>

                        {/* Citação — botão unificado com toggle inteligente */}
                        <button
                            className="et-btn"
                            onClick={handleQuoteClick}
                            title={isInsideBlockquote() ? 'Editar / Remover citação' : 'Inserir citação'}
                        >
                            <Quote size={16} />
                        </button>
                        {showQuotePicker && (
                            <div className="et-modal-overlay" onClick={() => setShowQuotePicker(false)}>
                                <div className="et-modal" style={{ maxWidth: 320 }} onClick={e => e.stopPropagation()}>
                                    <div className="et-modal-header">
                                        <h3>Citação</h3>
                                        <button className="et-modal-close" onClick={() => setShowQuotePicker(false)}><X size={18} /></button>
                                    </div>
                                    <div className="et-modal-body">
                                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Escolha uma cor para a citação:</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {quoteColors.map(c => (
                                                <button key={c.value} title={c.label} onClick={() => applyQuoteColor(c.value)}
                                                    style={{ width: 26, height: 26, borderRadius: '50%', background: c.value, border: '2px solid rgba(0,0,0,0.1)', cursor: 'pointer', outline: 'none' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="et-modal-actions">
                                        <button
                                            className="et-btn-cancel"
                                            style={{ color: '#ef4444', borderColor: '#fee2e2' }}
                                            onClick={removeQuote}
                                        >
                                            Remover citação
                                        </button>
                                        <button className="et-btn-cancel" onClick={() => setShowQuotePicker(false)}>Fechar</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Listas */}
                    <div className="toolbar-group">
                        <button className="et-btn" onClick={() => execCommand('insertUnorderedList')} title="Lista">
                            <List size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('insertOrderedList')} title="Lista Numerada">
                            <ListOrdered size={16} />
                        </button>

                        <div className="color-picker-wrapper">
                            <button
                                className="et-btn"
                                title="Cor dos marcadores da lista atual"
                                style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.5px' }}
                                onClick={() => {
                                    onSaveSelection();
                                    setShowMarkerPicker(!showMarkerPicker);
                                }}
                            >
                                •A
                            </button>
                            {showMarkerPicker && (
                                <div className="color-picker-dropdown marker-color-dropdown">
                                    {markerColors.map(color => (
                                        <button
                                            key={color}
                                            className="color-option"
                                            style={{
                                                background: color,
                                                border: color === '#ffffff' ? '1px solid #e5e7eb' : 'none'
                                            }}
                                            onClick={() => {
                                                const selection = window.getSelection();
                                                if (!selection || !editorRef.current) {
                                                    setShowMarkerPicker(false);
                                                    return;
                                                }
                                                let node: Node | null = selection.anchorNode;
                                                const lisToColor: HTMLElement[] = [];
                                                while (node && node !== editorRef.current) {
                                                    if ((node as HTMLElement).tagName === 'UL' || (node as HTMLElement).tagName === 'OL') {
                                                        (node as HTMLElement).querySelectorAll('li').forEach(li => lisToColor.push(li as HTMLElement));
                                                        break;
                                                    }
                                                    node = node.parentNode;
                                                }
                                                if (lisToColor.length === 0) {
                                                    alert('Posicione o cursor dentro de uma lista primeiro.');
                                                    setShowMarkerPicker(false);
                                                    return;
                                                }
                                                lisToColor.forEach(li => {
                                                    li.style.setProperty('--marker-color', color);
                                                });
                                                const inputEvent = new Event('input', { bubbles: true });
                                                editorRef.current!.dispatchEvent(inputEvent);
                                                setShowMarkerPicker(false);
                                            }}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Tamanho do marcador de lista */}
                        <select
                            className="et-font-size-select"
                            title="Tamanho dos marcadores da lista"
                            defaultValue=""
                            onChange={(e) => {
                                if (e.target.value) {
                                    onSaveSelection();
                                    applyListMarkerSize(e.target.value);
                                    e.target.value = '';
                                }
                            }}
                        >
                            <option value="">•sz</option>
                            <option value="10px">10</option>
                            <option value="12px">12</option>
                            <option value="14px">14</option>
                            <option value="16px">16</option>
                            <option value="18px">18</option>
                            <option value="20px">20</option>
                            <option value="24px">24</option>
                            <option value="28px">28</option>
                            <option value="32px">32</option>
                        </select>
                    </div>

                    {/* Recuo / Avanço */}
                    <div className="toolbar-group">
                        <button className="et-btn" onClick={handleOutdent} title="Recuar (Shift+Tab)">
                            <IndentDecrease size={16} />
                        </button>
                        <button className="et-btn" onClick={handleIndent} title="Avançar (Tab)">
                            <IndentIncrease size={16} />
                        </button>
                    </div>

                    {/* Alinhamento */}
                    <div className="toolbar-group">
                        <button className="et-btn" onClick={() => applyAlign('justifyLeft', 'left')} title="Esquerda">
                            <AlignLeft size={16} />
                        </button>
                        <button className="et-btn" onClick={() => applyAlign('justifyCenter', 'center')} title="Centro">
                            <AlignCenter size={16} />
                        </button>
                        <button className="et-btn" onClick={() => applyAlign('justifyRight', 'right')} title="Direita">
                            <AlignRight size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('justifyFull')} title="Justificado">
                            <AlignJustify size={16} />
                        </button>
                    </div>

                    {/* Sobrescrito/Subscrito */}
                    <div className="toolbar-group">
                        <button className="et-btn" onClick={() => execCommand('superscript')} title="Sobrescrito (x²)">
                            <Superscript size={16} />
                        </button>
                        <button className="et-btn" onClick={() => execCommand('subscript')} title="Subscrito (H₂O)">
                            <Subscript size={16} />
                        </button>
                    </div>

                    {/* Presets de estilo rápido — quadrados de cor, sem texto */}
                    <div className="toolbar-group">
                        <button
                            className="et-btn et-style-preset"
                            style={{ background: '#2a4a5a', width: 28, height: 28, padding: 0, border: '2px solid #2a4a5a', borderRadius: 5 }}
                            onClick={() => applyStylePreset('#2a4a5a', 'Georgia', '22px')}
                            title="Preset: Azul Prussiano — Georgia 22px Negrito"
                        />
                        <button
                            className="et-btn et-style-preset"
                            style={{ background: '#0D3B0E', width: 28, height: 28, padding: 0, border: '2px solid #0D3B0E', borderRadius: 5 }}
                            onClick={() => applyStylePreset('#0D3B0E', 'Georgia', '22px')}
                            title="Preset: Verde GRAIA — Georgia 22px Negrito"
                        />
                    </div>

                    {/* Mídia — botão unificado com dropdown */}
                    <div className="toolbar-group">
                        <div className="color-picker-wrapper">
                            <button
                                className="et-btn"
                                onClick={() => {
                                    onSaveSelection();
                                    setShowMediaDropdown(v => !v);
                                }}
                                title="Inserir Mídia"
                                style={{ gap: 4, paddingRight: 6 }}
                            >
                                <ImagePlus size={16} />
                                <ChevronDown size={11} />
                            </button>
                            {showMediaDropdown && (
                                <div className="et-media-dropdown">
                                    {[
                                        { label: 'Imagem', icon: Image, action: () => { setMediaType('image'); setShowMediaModal(true); setShowMediaDropdown(false); } },
                                        { label: 'GIF', icon: Film, action: () => { setMediaType('gif'); setShowMediaModal(true); setShowMediaDropdown(false); } },
                                        { label: 'Vídeo', icon: Video, action: () => { setMediaType('video'); setShowMediaModal(true); setShowMediaDropdown(false); } },
                                        { label: 'Áudio', icon: Music, action: () => { setShowAudioModal(true); setShowMediaDropdown(false); } },
                                    ].map(({ label, icon: Icon, action }) => (
                                        <button key={label} className="et-media-opt" onClick={action}>
                                            <Icon size={14} />
                                            <span>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            className="et-btn"
                            onClick={() => {
                                onSaveSelection();
                                setShowLinkModal(true);
                            }}
                            title="Inserir Link Interno"
                        >
                            <Link2 size={16} />
                        </button>
                    </div>

                    {/* Tabela */}
                    <div className="toolbar-group">
                        <button
                            className="et-btn"
                            onClick={() => {
                                onSaveSelection();
                                setShowTableModal(true);
                            }}
                            title="Inserir Tabela"
                        >
                            <Table size={16} />
                        </button>
                    </div>

                    {/* Caixa de Destaque */}
                    <div className="toolbar-group">
                        <button
                            className="et-btn"
                            onClick={() => {
                                onSaveSelection();
                                setShowCalloutModal(true);
                            }}
                            title="Inserir Caixa de Destaque"
                        >
                            <StickyNote size={16} />
                        </button>
                    </div>

                    {/* Flashcard */}
                    {cards.length > 0 && (
                        <div className="toolbar-group">
                            <button
                                className="et-btn"
                                onClick={() => {
                                    onSaveSelection();
                                    setShowCardModal(true);
                                }}
                                title="Inserir Flashcard"
                            >
                                <Sparkles size={16} />
                            </button>
                        </div>
                    )}

                    {/* Quiz */}
                    <div className="toolbar-group">
                        <button
                            className="et-btn"
                            onClick={() => {
                                onSaveSelection();
                                setShowQuizColorPicker(!showQuizColorPicker);
                            }}
                            title="Inserir Quiz"
                            disabled={!quizManager}
                        >
                            <FileQuestion size={16} />
                        </button>
                        {showQuizColorPicker && (
                            <div className="et-modal-overlay" onClick={() => setShowQuizColorPicker(false)}>
                                <div className="et-modal" style={{ maxWidth: 320 }} onClick={e => e.stopPropagation()}>
                                    <div className="et-modal-header">
                                        <h3>Cor do Quiz</h3>
                                        <button className="et-modal-close" onClick={() => setShowQuizColorPicker(false)}><X size={18} /></button>
                                    </div>
                                    <div className="et-modal-body">
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                                            {quizColors.map(c => (
                                                <button key={c.value} title={c.label} onClick={() => setQuizColor(c.value)}
                                                    style={{ width: 26, height: 26, borderRadius: '50%', background: c.value, border: quizColor === c.value ? '3px solid #111' : '2px solid rgba(0,0,0,0.1)', cursor: 'pointer', outline: 'none', transform: quizColor === c.value ? 'scale(1.2)' : 'scale(1)', transition: 'transform 0.1s' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="et-modal-actions">
                                        <button className="et-btn-cancel" onClick={() => setShowQuizColorPicker(false)}>Cancelar</button>
                                        <button className="et-btn-confirm" onClick={() => { handleInsertQuiz(); setShowQuizColorPicker(false); }}>Inserir Quiz</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Flashcards */}
            {showCardModal && (
                <div className="et-modal-overlay" onClick={() => setShowCardModal(false)}>
                    <div className="et-modal et-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <h3>Selecionar Flashcard</h3>
                            <button className="et-modal-close" onClick={() => setShowCardModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="et-modal-body">

                            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Cor do badge:</span>
                                {badgeColors.map(c => (
                                    <button
                                        key={c.value}
                                        title={c.label}
                                        onClick={() => setBadgeColor(c.value)}
                                        style={{
                                            width: 22, height: 22,
                                            borderRadius: '50%',
                                            background: c.value,
                                            border: badgeColor === c.value ? '3px solid #111' : '2px solid rgba(0,0,0,0.1)',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            transition: 'transform 0.1s',
                                            transform: badgeColor === c.value ? 'scale(1.2)' : 'scale(1)',
                                        }}
                                    />
                                ))}
                            </div>

                            <div className="et-cards-grid">
                                {cards.map((card, index) => (
                                    <div
                                        key={card._id || index}
                                        className="et-card-item"
                                        onClick={() => insertFlashcardBadge(card, index)}
                                    >
                                        <div className="et-card-label">CARD {index + 1}</div>
                                        <div className="et-card-front">
                                            {card.front.substring(0, 80)}
                                            {card.front.length > 80 ? '...' : ''}
                                        </div>
                                        <div className="et-card-back">
                                            {card.back.substring(0, 60)}
                                            {card.back.length > 60 ? '...' : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Mídia */}
            {showMediaModal && (
                <div className="et-modal-overlay" onClick={() => setShowMediaModal(false)}>
                    <div className="et-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <h3>
                                Inserir {mediaType === 'image' ? 'Imagem' : mediaType === 'gif' ? 'GIF' : 'Vídeo'}
                            </h3>
                            <button className="et-modal-close" onClick={() => setShowMediaModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="et-modal-body">
                            <label>URL</label>
                            <input
                                type="text"
                                className="et-modal-input"
                                placeholder={
                                    mediaType === 'video'
                                        ? 'https://youtube.com/watch?v=... ou URL do vídeo'
                                        : 'https://...'
                                }
                                value={mediaUrl}
                                onChange={(e) => setMediaUrl(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && mediaUrl.trim()) {
                                        insertMedia(mediaType, mediaUrl, mediaCaption || undefined);
                                    }
                                }}
                            />

                            <div className="et-modal-divider">OU</div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={mediaType === 'video' ? 'video/*' : 'image/*,image/gif'}
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                            <button
                                className="et-btn-file"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Escolher Arquivo
                            </button>

                            <div className="et-modal-field">
                                <label>Legenda (opcional)</label>
                                <input
                                    type="text"
                                    className="et-modal-input"
                                    placeholder="Ex: Figura 1 - Descrição"
                                    value={mediaCaption}
                                    onChange={(e) => setMediaCaption(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="et-modal-actions">
                            <button className="et-btn-cancel" onClick={() => setShowMediaModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="et-btn-confirm"
                                onClick={() => {
                                    if (mediaUrl.trim()) {
                                        insertMedia(mediaType, mediaUrl, mediaCaption || undefined);
                                    }
                                }}
                                disabled={!mediaUrl.trim()}
                            >
                                Inserir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Link Interno */}
            {showLinkModal && (
                <div className="et-modal-overlay" onClick={() => setShowLinkModal(false)}>
                    <div className="et-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <h3>Link Interno</h3>
                            <button className="et-modal-close" onClick={() => setShowLinkModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="et-modal-body">
                            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
                                Selecione um texto no editor antes de inserir o link.
                            </p>

                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
                                <button
                                    onClick={() => setLinkTab('page')}
                                    style={{
                                        flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                        background: linkTab === 'page' ? 'white' : 'transparent',
                                        color: linkTab === 'page' ? 'var(--platform-accent)' : '#6b7280',
                                        boxShadow: linkTab === 'page' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    Mesma apostila
                                </button>
                                <button
                                    onClick={() => setLinkTab('apostila')}
                                    style={{
                                        flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                        background: linkTab === 'apostila' ? 'white' : 'transparent',
                                        color: linkTab === 'apostila' ? 'var(--platform-accent)' : '#6b7280',
                                        boxShadow: linkTab === 'apostila' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    Outra apostila
                                </button>
                            </div>

                            {linkTab === 'page' ? (
                                pages.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                                        {pages.map((page, index) => (
                                            <button
                                                key={page.id}
                                                onClick={() => setLinkTargetPageId(page.id)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                                    border: `2px solid ${linkTargetPageId === page.id ? 'var(--platform-accent)' : '#e5e7eb'}`,
                                                    borderRadius: 8, background: linkTargetPageId === page.id ? 'var(--platform-a08)' : 'white',
                                                    cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                                                }}
                                            >
                                                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', minWidth: 20 }}>{index + 1}</span>
                                                <span style={{ fontSize: 13, fontWeight: 500, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {page.title || `Página ${index + 1}`}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '16px 0' }}>
                                        Nenhuma página disponível
                                    </p>
                                )
                            ) : (
                                <div className="et-modal-field">
                                    <label>URL da apostila</label>
                                    <input
                                        type="text"
                                        className="et-modal-input"
                                        placeholder="Ex: /resumo/deckId/subdeckId"
                                        value={linkExternalUrl}
                                        onChange={e => setLinkExternalUrl(e.target.value)}
                                    />
                                    <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                                        Cole o caminho da apostila. Ao clicar no link, abre em nova aba.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="et-modal-actions">
                            <button className="et-btn-cancel" onClick={() => setShowLinkModal(false)}>Cancelar</button>
                            <button
                                className="et-btn-confirm"
                                onClick={insertInternalLink}
                                disabled={linkTab === 'page' ? !linkTargetPageId : !linkExternalUrl.trim()}
                            >
                                Inserir Link
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Áudio */}
            {showAudioModal && (
                <div className="et-modal-overlay" onClick={() => setShowAudioModal(false)}>
                    <div className="et-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <h3>Inserir Áudio</h3>
                            <button className="et-modal-close" onClick={() => setShowAudioModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="et-modal-body">
                            <label>URL do áudio</label>
                            <input
                                type="text"
                                className="et-modal-input"
                                placeholder="https://... (mp3, ogg, wav)"
                                value={audioUrl}
                                onChange={(e) => setAudioUrl(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && audioUrl.trim()) {
                                        insertAudioHTML(audioUrl.trim(), audioCaption || undefined);
                                    }
                                }}
                            />

                            <div className="et-modal-divider">OU</div>

                            <input
                                ref={audioFileInputRef}
                                type="file"
                                accept="audio/*"
                                onChange={handleAudioFileUpload}
                                style={{ display: 'none' }}
                            />
                            <button
                                className="et-btn-file"
                                onClick={() => { setAudioUploadError(''); audioFileInputRef.current?.click(); }}
                                disabled={audioUploading}
                            >
                                {audioUploading ? 'Enviando para Cloudinary...' : 'Escolher Arquivo de Áudio'}
                            </button>

                            {audioUploadError && (
                                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>
                                    {audioUploadError}
                                </p>
                            )}

                            <div className="et-modal-field">
                                <label>Legenda (opcional)</label>
                                <input
                                    type="text"
                                    className="et-modal-input"
                                    placeholder="Ex: Ausculta cardíaca normal"
                                    value={audioCaption}
                                    onChange={(e) => setAudioCaption(e.target.value)}
                                    disabled={audioUploading}
                                />
                            </div>
                        </div>
                        <div className="et-modal-actions">
                            <button className="et-btn-cancel" onClick={() => { setShowAudioModal(false); setAudioUploadError(''); }}>
                                Cancelar
                            </button>
                            <button
                                className="et-btn-confirm"
                                onClick={() => {
                                    if (audioUrl.trim()) {
                                        insertAudioHTML(audioUrl.trim(), audioCaption || undefined);
                                    }
                                }}
                                disabled={!audioUrl.trim() || audioUploading}
                            >
                                Inserir URL
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showTableModal && (
                <TableInsertModal
                    darkMode={darkMode}
                    onInsert={(config) => {
                        if (!tableManager) {
                            console.error('❌ TableManager não disponível');
                            return;
                        }
                        tableManager.insertTable(config, savedRangeRef.current);
                        setShowTableModal(false);
                    }}
                    onClose={() => setShowTableModal(false)}
                />
            )}

            {/* Modal de Caixa de Destaque */}
            {showCalloutModal && (
                <div className="et-modal-overlay" onClick={() => setShowCalloutModal(false)}>
                    <div className="et-modal et-modal-callout" onClick={e => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <h3>Inserir Caixa de Destaque</h3>
                            <button className="et-modal-close" onClick={() => setShowCalloutModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="et-callout-modal-body">
                            {/* LEFT: Formulário */}
                            <div className="et-callout-form">

                                {/* Presets de cor */}
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                                    Estilo de cor
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                                    {calloutPresets.map(preset => (
                                        <button
                                            key={preset.border}
                                            title={preset.label}
                                            onClick={() => {
                                                setCalloutColor(preset.border);
                                                setCalloutBgColor(preset.bg);
                                            }}
                                            style={{
                                                width: 28, height: 28, borderRadius: 6,
                                                background: preset.bg, border: `3px solid ${preset.border}`,
                                                cursor: 'pointer',
                                                outline: calloutColor === preset.border ? `2px solid #111` : 'none',
                                                transform: calloutColor === preset.border ? 'scale(1.15)' : 'scale(1)',
                                                transition: 'transform 0.1s',
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Ícone */}
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                                    Ícone (opcional)
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                                    {calloutIcons.map(icon => (
                                        <button
                                            key={icon.value}
                                            title={icon.label}
                                            onClick={() => setCalloutIcon(icon.value)}
                                            style={{
                                                width: 36, height: 36, borderRadius: 6,
                                                border: `2px solid ${calloutIcon === icon.value ? calloutColor : '#e5e7eb'}`,
                                                background: calloutIcon === icon.value ? `${calloutColor}18` : 'white',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.15s',
                                                transform: calloutIcon === icon.value ? 'scale(1.1)' : 'scale(1)',
                                                color: calloutIcon === icon.value ? calloutColor : '#6b7280',
                                                fontSize: 11, fontWeight: 600,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: icon.value
                                                    ? getCalloutIconSvg(icon.value, calloutIcon === icon.value ? calloutColor : '#6b7280')
                                                    : '∅'
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Estilo de borda */}
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                                    Estilo de borda
                                </label>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                    {(['left', 'full', 'top'] as const).map(style => (
                                        <button
                                            key={style}
                                            onClick={() => setCalloutBorderStyle(style)}
                                            style={{
                                                padding: '6px 14px', borderRadius: 6,
                                                border: `2px solid ${calloutBorderStyle === style ? calloutColor : '#e5e7eb'}`,
                                                background: calloutBorderStyle === style ? `${calloutColor}18` : 'white',
                                                color: calloutBorderStyle === style ? calloutColor : '#374151',
                                                fontWeight: 600, fontSize: 12, cursor: 'pointer',
                                            }}
                                        >
                                            {style === 'left' ? '▌ Esquerda' : style === 'full' ? '□ Completa' : '▀ Topo'}
                                        </button>
                                    ))}
                                </div>

                                {/* Título */}
                                <div className="et-modal-field">
                                    <label>Título (opcional)</label>
                                    <div style={{ border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', gap: 2, padding: '6px 8px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                            {([
                                                { cmd: 'bold', label: 'N', style: { fontWeight: 700 }, title: 'Negrito' },
                                                { cmd: 'italic', label: 'I', style: { fontStyle: 'italic' }, title: 'Itálico' },
                                                { cmd: 'underline', label: 'S', style: { textDecoration: 'underline' }, title: 'Sublinhado' },
                                            ] as { cmd: string; label: string; style: React.CSSProperties; title: string }[]).map(({ cmd, label, style, title }) => (
                                                <button
                                                    key={cmd}
                                                    title={title}
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        calloutTitleRef.current?.focus();
                                                        document.execCommand(cmd, false);
                                                    }}
                                                    style={{
                                                        width: 30, height: 30, border: '1px solid transparent',
                                                        borderRadius: 5, background: 'transparent', cursor: 'pointer',
                                                        fontSize: 14, color: '#374151', display: 'inline-flex',
                                                        alignItems: 'center', justifyContent: 'center',
                                                        fontFamily: 'inherit', ...style,
                                                    }}
                                                >{label}</button>
                                            ))}
                                        </div>
                                        <div
                                            ref={calloutTitleRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={e => setCalloutTitle(e.currentTarget.innerHTML)}
                                            className="et-callout-title-editor"
                                            style={{
                                                minHeight: 36, padding: '8px 14px', fontSize: 14, fontWeight: 600,
                                                color: '#111827', fontFamily: 'inherit', outline: 'none', lineHeight: 1.6,
                                            }}
                                            data-placeholder="Ex: Fatores de risco resumidos"
                                        />
                                    </div>
                                </div>

                                {/* Conteúdo com editor rico */}
                                <div className="et-modal-field">
                                    <label>Conteúdo</label>
                                    <div style={{ border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
                                        {/* Mini toolbar */}
                                        <div style={{ display: 'flex', gap: 2, padding: '6px 8px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                            {([
                                                { cmd: 'bold', label: 'N', style: { fontWeight: 700 }, title: 'Negrito' },
                                                { cmd: 'italic', label: 'I', style: { fontStyle: 'italic' }, title: 'Itálico' },
                                                { cmd: 'underline', label: 'S', style: { textDecoration: 'underline' }, title: 'Sublinhado' },
                                            ] as { cmd: string; label: string; style: React.CSSProperties; title: string }[]).map(({ cmd, label, style, title }) => (
                                                <button
                                                    key={cmd}
                                                    title={title}
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        calloutEditorRef.current?.focus();
                                                        document.execCommand(cmd, false);
                                                    }}
                                                    style={{
                                                        width: 30, height: 30, border: '1px solid transparent',
                                                        borderRadius: 5, background: 'transparent', cursor: 'pointer',
                                                        fontSize: 14, color: '#374151', display: 'inline-flex',
                                                        alignItems: 'center', justifyContent: 'center',
                                                        fontFamily: 'inherit', ...style,
                                                    }}
                                                >{label}</button>
                                            ))}
                                            <div style={{ width: 1, background: '#d1d5db', margin: '4px 2px' }} />
                                            {([
                                                { cmd: 'insertUnorderedList', label: '•', title: 'Lista com marcadores', fontSize: 16 },
                                                { cmd: 'insertOrderedList', label: '1.', title: 'Lista numerada', fontSize: 11 },
                                            ] as { cmd: string; label: string; title: string; fontSize: number }[]).map(({ cmd, label, title, fontSize }) => (
                                                <button
                                                    key={cmd}
                                                    title={title}
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        calloutEditorRef.current?.focus();
                                                        document.execCommand(cmd, false);
                                                    }}
                                                    style={{
                                                        width: 30, height: 30, border: '1px solid transparent',
                                                        borderRadius: 5, background: 'transparent', cursor: 'pointer',
                                                        fontSize, fontWeight: 700, color: '#374151',
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        fontFamily: 'inherit',
                                                    }}
                                                >{label}</button>
                                            ))}
                                        </div>
                                        {/* Editor contenteditable */}
                                        <div
                                            ref={calloutEditorRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={e => setCalloutText(e.currentTarget.innerHTML)}
                                            className="et-callout-rich-editor"
                                            style={{
                                                minHeight: 100, maxHeight: 220, overflowY: 'auto',
                                                padding: '10px 14px', fontSize: 14, color: '#111827',
                                                fontFamily: 'inherit', outline: 'none', lineHeight: 1.6,
                                            }}
                                            data-placeholder="Digite o conteúdo da caixa..."
                                        />
                                    </div>
                                </div>

                                {/* Modo Gabarito */}
                                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#374151' }}>
                                        Modo Gabarito
                                    </label>
                                    <button
                                        onClick={() => setCalloutIsGabarito(v => !v)}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                            width: '100%', padding: '9px 14px', borderRadius: 8,
                                            border: `2px solid ${calloutIsGabarito ? calloutColor : '#e5e7eb'}`,
                                            background: calloutIsGabarito ? `${calloutColor}18` : 'white',
                                            color: calloutIsGabarito ? calloutColor : '#6b7280',
                                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                            fontFamily: 'inherit', transition: 'all 0.15s',
                                        }}
                                    >
                                        👁 {calloutIsGabarito ? 'Gabarito ativado — caixa ficará embaçada' : 'Transformar em gabarito'}
                                    </button>
                                    {calloutIsGabarito && (
                                        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
                                            O conteúdo ficará embaçado. O leitor clica em "Mostrar resposta" para revelar.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT: Preview */}
                            <div className="et-callout-preview-pane">
                                <span className="et-callout-preview-label">Prévia</span>
                                <div style={{
                                    borderLeft: calloutBorderStyle === 'left' ? `4px solid ${calloutColor}` : undefined,
                                    border: calloutBorderStyle === 'full' ? `2px solid ${calloutColor}` : undefined,
                                    borderTop: calloutBorderStyle === 'top' ? `4px solid ${calloutColor}` : undefined,
                                    background: calloutBgColor,
                                    borderRadius: 8,
                                    padding: '12px 16px',
                                }}>
                                    {(calloutTitle.replace(/<[^>]+>/g, '').trim() || calloutIcon) && (
                                        <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 14, color: calloutColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {calloutIcon && (
                                                <span
                                                    style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                                    dangerouslySetInnerHTML={{ __html: getCalloutIconSvg(calloutIcon, calloutColor) }}
                                                />
                                            )}
                                            <span dangerouslySetInnerHTML={{ __html: calloutTitle }} />
                                        </p>
                                    )}
                                    <div
                                        style={{ margin: 0, fontSize: 13, color: calloutColor, lineHeight: 1.6 }}
                                        dangerouslySetInnerHTML={{ __html: calloutText || '<span style="opacity:0.5">Prévia da caixa de destaque...</span>' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="et-modal-actions">
                            <button className="et-btn-cancel" onClick={() => setShowCalloutModal(false)}>
                                Cancelar
                            </button>
                            <button className="et-btn-confirm" onClick={insertCallout}>
                                Inserir Caixa
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditorToolbar;